import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import { globalErrorHandler } from '../error/saga';
import { api } from '../../configurations/api';
import {
    getSignedUrlForThougthUrl,
    initiateThougthMultipartUploadUrl,
    likeUrl,
    thougthUrl,
} from '../../configurations/api/url';
import { httpRequest } from '../types';
import { filterOutNotAllowed, showToastMessage } from '../../utils/AppUtils';
import {
    ICompleteMediaUpload,
    ICreateThougth,
    IGetThougth,
    IGetThougths,
    IReactToThougth,
    IUpdateThougth,
    IUploadThougthMedia,
    types,
} from './types';
import { getUploadSession } from '../upload/action';
import {
    createThoughtFail,
    createThoughtSuccess,
    deleteThougthFail,
    deleteThougthLikeFail,
    deleteThougthLikeSuccess,
    deleteThougthSuccess,
    getThougthFail,
    getThougthSuccess,
    likeThougthSuccess,
    likeThougthtFail,
    setMediaUpload,
    updateThougthFail,
    updateThougthSuccess,
    uploadThougthMedia as uploadThougthMediaAction,
    uploadThougthMediaSuccess,
    updateThougth as updateThougthAction,
    uploadThougthMediaFail,
    publishThougthSuccess,
    unpublishThougthSuccess,
    unpublishThougthFail,
    publishThougth as publishThougthAction,
} from './action';
import { SentryCapture } from '../../analytics/Sentry';
import { destroyOneCache } from '../cache/action';
import { CACHE_TYPE } from '../cache/types';
import { isEmpty } from '../../utils/lodash';
import { RootState } from '../root-reducer';
import { TUploadCategory } from '../upload/types';
import { IGenericDocumentModifier, IThougth } from '../../types/global/helper';
import axios from 'axios';
import { publishAuditionFail } from '../audition/actions';

interface IMediaPayload {
    audios?: string[];
    videos?: string[];
    files?: string[];
    images?: string[];
}

function filterNonEmptyMedia(thougth: IThougth): IMediaPayload {
    const { audios, videos, files, images } = thougth;

    const mediaPayload: IMediaPayload = {};

    if (audios && audios.length > 0) {
        mediaPayload.audios = audios;
    }

    if (videos && videos.length > 0) {
        mediaPayload.videos = videos;
    }

    if (files && files.length > 0) {
        mediaPayload.files = files;
    }

    if (images && images.length > 0) {
        mediaPayload.images = images;
    }

    return mediaPayload;
}

function* createThougth({ payload }: { payload: ICreateThougth }): any {
    const filteredBody = filterOutNotAllowed(payload, 'videos', 'images', 'audios', 'files');
    try {
        const response = yield call(api, `${thougthUrl}`, httpRequest.POST, filteredBody, 0, 1000, false);
        const { data } = response.data;

        const uploadPayload = {
            thougthId: data._id,
            videos: payload.videos,
            images: payload.images,
            audios: payload.audios,
            files: payload.files,
        };

        yield put(
            setMediaUpload({
                thougth: data,
            }),
        );
        if (!isEmpty(payload.images) || !isEmpty(payload.files) || !isEmpty(payload.audios)) {
            yield call(uploadThougthSmallerMedia, { payload: uploadPayload } as any);
        }

        if (!isEmpty(payload.videos)) {
            /** Trigger Upload */
            yield put(
                uploadThougthMediaAction({
                    thougthId: data._id,
                    videos: uploadPayload.videos,
                    images: uploadPayload.images,
                    audios: uploadPayload.audios,
                    files: uploadPayload.files,
                }),
            );
        } else {
            yield put(createThoughtSuccess(data));
            yield call(showToastMessage, 'Post created successfully', 'success');
        }

        if (payload.publishStatus === 'publish') {
            yield put(publishThougthAction(data._id));
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createThoughtFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* publishThougth({ payload }: { payload: string }): any {
    const requestUrl = `${thougthUrl}/publish/${payload}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.PATCH, {}, 0, 0);
        const { data } = response;
        yield put(publishThougthSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(publishAuditionFail(error));
    }
}

function* unPublishThougth({ payload }: { payload: string }): any {
    const requestUrl = `${thougthUrl}/unpublish/${payload}`;
    try {
        const response = yield call(api, requestUrl, httpRequest.PATCH, {}, 0, 0);
        const { data } = response;
        yield put(unpublishThougthSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(unpublishThougthFail(error));
    }
}

/** This should work to upload ONLY Videos */
function* uploadThougthMedia({ payload }: { payload: IUploadThougthMedia }) {
    const { thougthId, videos = [] } = payload;

    // Helper function to determine file category
    const categorizeFile = (file: any, category: string) => ({
        file,
        category,
    });

    // Combine all media files into a single array with categories
    const allMedia = [...videos.map(video => categorizeFile(video, 'videos'))];

    // Check if there are any items to process
    if (allMedia.length > 0) {
        for (let i = 0; i < allMedia.length; i++) {
            const { file, category } = allMedia[i];
            const isLastItem = i === allMedia.length - 1; // Check if this is the last item

            yield put(
                getUploadSession({
                    projectId: thougthId,
                    project_file: file,
                    initialPayload: payload as any,
                    type: 'thougth',
                    url: initiateThougthMultipartUploadUrl,
                    isLastItem, // Pass the flag
                    category: category as TUploadCategory, // Pass the category
                }),
            );
        }
    }
}

function* completeThougthMediaUpload({ payload }: { payload: ICompleteMediaUpload }): any {
    const state: RootState = yield select();
    const { thougthInCreation } = state.thougth;
    const body = filterNonEmptyMedia(thougthInCreation!);

    try {
        const response = yield call(api, `${thougthUrl}/${thougthInCreation?._id}`, httpRequest.PATCH, body, 0, 1000);
        const { data } = response.data;
        yield put(uploadThougthMediaSuccess(data));
        yield put(createThoughtSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateThougthFail(error));
        yield call(globalErrorHandler, error);
    }
}
function* updateThougth({ payload }: { payload: IUpdateThougth }): any {
    const body = payload.body;

    try {
        const response = yield call(api, `${thougthUrl}/${payload.thoughtId}`, httpRequest.PATCH, body, 0, 1000);
        const { data } = response.data;
        yield put(updateThougthSuccess(data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.THOUGTHS }));
        if (payload.fromUpload) {
            yield put(createThoughtSuccess(data));
            yield put(uploadThougthMediaSuccess(data));
            yield call(showToastMessage, 'Post created successfully', 'success');
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateThougthFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getOneThougth({ payload }: { payload: IGetThougth }): Generator<any, any, any> {
    try {
        const response = yield call(api, `${thougthUrl}/${payload.thougthId}`, httpRequest.GET, null, 3, 1000);
        const { data } = response.data;
        yield put(getThougthSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getThougthFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getAllThougths({ payload }: { payload: IGetThougths }): Generator<any, any, any> {
    const query = new URLSearchParams();
    if (payload.limit) query.append('limit', String(payload.limit));
    if (payload.page) query.append('page', String(payload.page));
    try {
        const response = yield call(api, `${thougthUrl}?${query}`, httpRequest.GET, null, 3, 1000);
        const { data } = response.data;
        yield put(getThougthSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getThougthFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteThougth({ payload }: { payload: IGenericDocumentModifier }): Generator<any, any, any> {
    try {
        yield call(api, `${thougthUrl}/${payload.documentId}`, httpRequest.DELETE, null, 3, 1000);
        yield put(deleteThougthSuccess(payload));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.THOUGTHS }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteThougthFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* likeAThougth({ payload }: { payload: IReactToThougth }): any {
    try {
        const { like_category, like_type, thoughtId, like_origin } = payload;
        const request = `${thougthUrl}/${thoughtId}/like`;
        const response = yield call(api, request, httpRequest.POST, { like_category, like_type }, 0, 2000, false);
        const { data } = response.data;
        yield put(likeThougthSuccess({ data: data, like_origin: like_origin }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(
            likeThougthtFail({
                documentId: payload.thoughtId,
                ...error,
            }),
        );
    }
}

function* deleteAThougthLike({ payload }: { payload: IGenericDocumentModifier }): any {
    const { documentId, additionalPayload, morePayload } = payload;
    try {
        const request = `${likeUrl}/${documentId}`;
        yield call(api, request, httpRequest.DELETE, {}, 2, 2000, false);
        yield put(deleteThougthLikeSuccess(payload));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(
            deleteThougthLikeFail({
                ...error,
                documentId: additionalPayload,
                morePayload: morePayload,
            }),
        );
    }
}

export const getFileExtension = (file: File | string) => {
    if (typeof file === 'string') {
        const matches = file.match(/^data:(.+);base64,/);

        if (matches && matches.length > 1) {
            // Extract the MIME type from the metadata and return the file extension
            const mimeType = matches[1];
            // Get the file extension by splitting the MIME type
            return mimeType.split('/')[1];
        }
    }

    if (typeof file === 'object') {
        const fileName = file.name;
        return fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    }

    return 'jpeg';
};

function* uploadThougthSmallerMedia({ payload }: { payload: IUploadThougthMedia }): any {
    const { thougthId, audios, files, images } = payload;

    console.log('I Made i here to seee what is going on');

    const uploadMedia = function* (mediaArray: File[], category: string): any {
        const keys: string[] = [];
        for (const media of mediaArray) {
            try {
                const featureKey = yield call(
                    api,
                    `${getSignedUrlForThougthUrl}/${thougthId}?extension=${getFileExtension(media)}`,
                    httpRequest.GET,
                    {},
                    2,
                    2000,
                );

                const file = new Blob([media]);
                const res = yield axios
                    .put(featureKey.data.url, file, {
                        onUploadProgress: progress => {
                            console.log(progress);
                        },
                        maxContentLength: Infinity,
                        withCredentials: false,
                        'axios-retry': {
                            retries: 2,
                        },
                    })
                    .catch(e => {
                        SentryCapture(e, 'error');
                    });

                if (res && res.statusText === 'OK') {
                    keys.push(featureKey.data.videoKey);
                }
            } catch (error: any) {
                SentryCapture(error, 'error');
                yield put(uploadThougthMediaFail(error));
                yield call(globalErrorHandler, error);
            }
        }
        return keys;
    };

    try {
        const result: any = {
            audios: [],
            files: [],
            images: [],
        };

        if (audios && audios?.length > 0) {
            result.audios = yield* uploadMedia(audios, 'audio');
        }

        if (files && files?.length > 0) {
            result.files = yield* uploadMedia(files, 'file');
        }

        if (images && images?.length > 0) {
            result.images = yield* uploadMedia(images, 'image');
        }

        // Only update the database once after all uploads are complete
        if (
            (!!audios && result.audios && result.audios.length) > 0 ||
            (!!files && result.files && result.files.length > 0) ||
            (!!images && result.images && result.images.length > 0)
        ) {
            yield put(
                updateThougthAction({
                    body: {
                        ...result,
                    },
                    thoughtId: thougthId,
                    fromUpload: true,
                }),
            );
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(uploadThougthMediaFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* handleUploadThoughtMedia(action: any) {
    try {
        // Run both sagas in parallel
        yield all([call(uploadThougthMedia, action)]);
    } catch (error) {
        // Handle errors if necessary
        console.error('Error in handling upload:', error);
    }
}

function* createThougthWatcher() {
    yield takeLatest<any>(types.CREATE_THOUGTH, createThougth);
}

function* publishThougthWatcher() {
    yield takeLatest<any>(types.PUBLISH_THOUGTH, publishThougth);
}

function* unPublishThougthWatcher() {
    yield takeLatest<any>(types.UNPUBLISH_THOUGTH, unPublishThougth);
}

function* uploadThougthMediaWatcher() {
    yield takeLatest<any>(types.UPLOAD_THOUGTH_MEDIA, handleUploadThoughtMedia);
}

function* completeThougthMediaUploadWatcher() {
    yield takeLatest<any>(types.COMPLETE_MEDIA_UPLOAD, completeThougthMediaUpload);
}

function* updateThougthWatcher() {
    yield takeLatest<any>(types.UPDATE_THOUGTH, updateThougth);
}

function* getOneThougthWatcher() {
    yield takeLatest<any>(types.GET_THOUGTH, getOneThougth);
}

function* getAllThougthsWatcher() {
    yield takeLatest<any>(types.GET_ALL_THOUGTHS, getAllThougths);
}

function* deleteThougthWatcher() {
    yield takeLatest<any>(types.DELETE_THOUGTH, deleteThougth);
}

function* likeAThougthWatcher() {
    yield takeLatest<any>(types.LIKE_A_THOUGTH, likeAThougth);
}

function* deleteAThougthLikeWatcher() {
    yield takeLatest<any>(types.DELETE_A_THOUGTH_LIKE, deleteAThougthLike);
}

export default function* thougthSaga() {
    yield all([
        createThougthWatcher(),
        uploadThougthMediaWatcher(),
        completeThougthMediaUploadWatcher(),
        updateThougthWatcher(),
        getOneThougthWatcher(),
        getAllThougthsWatcher(),
        deleteThougthWatcher(),
        likeAThougthWatcher(),
        deleteAThougthLikeWatcher(),
        publishThougthWatcher(),
        unPublishThougthWatcher(),
    ]);
}
